import { Link } from 'react-router-dom'
import { Select } from '@mantine/core'
import { IconBuilding, IconStack2, type Icon } from '@tabler/icons-react'
import type { EntradaResponse } from '@/api/chamados'
import { WidgetCard } from '@/components/ui/WidgetCard'
import { CardHeader } from '@/components/ui/CardHeader'
import classes from './Entrada.module.css'

interface ItemDoFiltro { id: number; nome: string | null; total: number }

interface CartaoDeFiltroProps {
  titulo: string
  /** Nome do item quando o servidor não traz (`Organização #3`). */
  nomeSemNome: string
  rotuloDoSeletor: string
  placeholder: string
  icone: Icon
  testId: string
  itens: ItemDoFiltro[]
  ativa: number | null
  /** Endereço de um item (ou de nenhum), mantendo a pasta aberta. */
  hrefDe: (id: number | null) => string
  onEscolher: (id: number | null) => void
  /** No computador o cartão ocupa o que sobra da coluna e a lista rola por dentro; as Pastas
   *  ficam sempre inteiras (pedido de 21/10/2026). */
  rolagemPropria?: boolean
}

/** Abaixo disto o cartão não encolhe: a coluna inteira volta a rolar (tela baixa demais). */
const ALTURA_MINIMA_DO_CARTAO = 160

/**
 * Cartão de filtro da coluna da Entrada (o "Labels" do Mailbox, spec §3): um item por valor
 * com chamados na pasta aberta; clicar filtra a lista, clicar de novo tira o filtro. Sem cor
 * por item. Abaixo de lg vira um seletor (§3.1). Quem chama decide se o cartão aparece.
 */
function CartaoDeFiltro({
  titulo, nomeSemNome, rotuloDoSeletor, placeholder, icone: Icone, testId, itens, ativa, hrefDe,
  onEscolher, rolagemPropria = false,
}: CartaoDeFiltroProps) {
  const nome = (i: ItemDoFiltro) => i.nome ?? `${nomeSemNome} #${i.id}`
  return (
    <>
      <div
        data-testid={testId}
        className={classes.organizacoesCartao}
        style={rolagemPropria
          ? { flexShrink: 1, minHeight: ALTURA_MINIMA_DO_CARTAO, display: 'flex', flexDirection: 'column' }
          : undefined}
      >
        <WidgetCard
          p={0}
          style={rolagemPropria ? { minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
        >
          <CardHeader title={titulo} />
          <nav aria-label={titulo} style={rolagemPropria ? { minHeight: 0, overflowY: 'auto' } : undefined}>
            <ul className={classes.pastas}>
              {itens.map((i) => (
                <li key={i.id}>
                  <Link
                    to={hrefDe(i.id === ativa ? null : i.id)}
                    className={classes.pasta}
                    aria-current={i.id === ativa ? 'page' : undefined}
                    aria-label={`${nome(i)}, ${i.total === 1 ? '1 chamado' : `${i.total} chamados`}`}
                  >
                    <Icone size={16} aria-hidden />
                    <span className={classes.pastaRotulo}>{nome(i)}</span>
                    <span className={classes.contador}>{i.total}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </WidgetCard>
      </div>
      <div className={classes.organizacoesSeletor}>
        <Select
          label={rotuloDoSeletor}
          placeholder={placeholder}
          clearable
          data={itens.map((i) => ({ value: String(i.id), label: `${nome(i)} (${i.total})` }))}
          value={ativa != null ? String(ativa) : null}
          onChange={(v) => onEscolher(v ? Number(v) : null)}
          comboboxProps={{ withinPortal: true }}
        />
      </div>
    </>
  )
}

type FiltroProps = Pick<CartaoDeFiltroProps, 'ativa' | 'hrefDe' | 'onEscolher' | 'rolagemPropria'>

/** Organizações com chamados na pasta. Some para quem só vê uma organização. */
export function OrganizacoesCard({ organizacoes, ...props }: FiltroProps & {
  organizacoes: EntradaResponse['organizacoes']
}) {
  if (organizacoes.length <= 1 && props.ativa == null) return null
  return (
    <CartaoDeFiltro
      titulo="Organizações" nomeSemNome="Organização" rotuloDoSeletor="Organização"
      placeholder="Todas as organizações" icone={IconBuilding} testId="entrada-organizacoes"
      itens={organizacoes} {...props}
    />
  )
}

/**
 * Filas de quem olha com chamados na pasta (spec 2026-10-21 §5). Só nas pastas de fila e só
 * para quem é membro de duas filas ou mais: é quantas filas a pessoa tem que decide, não
 * quantas têm chamado (com três filas e chamado em uma, a caixa mostra aquela uma).
 */
export function FilasCard({ filas, minhasFilasTotal, ...props }: FiltroProps & {
  filas: EntradaResponse['filas']
  minhasFilasTotal: number
}) {
  if ((minhasFilasTotal < 2 || filas.length === 0) && props.ativa == null) return null
  return (
    <CartaoDeFiltro
      titulo="Filas" nomeSemNome="Fila" rotuloDoSeletor="Fila" placeholder="Todas as filas"
      icone={IconStack2} testId="entrada-filas" itens={filas} {...props}
    />
  )
}
